import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokOpsListToolbar from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import CategoryCascaderSelect from '@/tiktok-platform/components/CategoryCascaderSelect';
import TiktokSwitch from '@/tiktok-platform/components/TiktokSwitch';
import TiktokOpsCopyHoverMenu from '@/tiktok-platform/components/TiktokOpsCopyHoverMenu';
import TiktokTemplateScopeTabs from '@/tiktok-platform/components/TiktokTemplateScopeTabs';
import TiktokStyleNoBatchFilter from '@/tiktok-platform/components/TiktokStyleNoBatchFilter';
import TiktokTemplateNameBatchFilter from '@/tiktok-platform/components/TiktokTemplateNameBatchFilter';
import SizeChartTemplateCreateModal from '@/tiktok-platform/modules/templates/SizeChartTemplateCreateModal';
import useMaterialTemplatePortableActions from '@/tiktok-platform/modules/templates/useMaterialTemplatePortableActions';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { formatBeijingDateTime } from '@/tiktok-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/tiktok-platform/utils/cellDisplayTitle';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import { MATERIAL_TEMPLATE_TYPES } from '@/tiktok-platform/utils/tiktokMaterialTemplatePortable';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { matchTemplateName, templateNameTokens } from '@/tiktok-platform/utils/templateNameQuery';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import {
  buildTiktokWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  resolveCopySourceScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  rowCanDeleteScopedTemplate,
  rowCanEditScopedTemplate,
  templateCopiedToast,
} from '@/tiktok-platform/constants/tiktokTemplateScope';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/tiktok-platform/utils/tiktokTemplateVersionGuard';
import { submitLocalTiktokSizeChartToPlatform } from '@/tiktok-platform/utils/submitLocalTiktokSizeChartToPlatform';
import useTiktokTemplateScope from '@/tiktok-platform/hooks/useTiktokTemplateScope';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { usePlatformShopPick, resolveWriteShopId } from '@/hooks/usePlatformShopPick';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function rowKey(row) {
  return String(row?.id ?? '');
}

const COL_SPAN_FULL = 12;
const COL_SPAN_OFFICIAL = 9;
const SIZE_CHART_SYNC_KINDS = ['sizeCharts'];

function sourceLabel(row) {
  const src = String(row?.source || '').toLowerCase();
  if (src === 'platform' || row?.platformBusinessId) return i18n.t('sizeChart.sourcePlatform', { ns: 'workbench' });
  return i18n.t('sizeChart.sourceLocal', { ns: 'workbench' });
}

/** 本地尺码（未落平台 businessId）可提交后台；提交成功后删除本地，避免与官方/平台重复 */
function canSubmitLocalSizeChart(row, { isOfficialTab = false } = {}) {
  if (isOfficialTab) return false;
  if (!row?.id) return false;
  if (row?.platformBusinessId) return false;
  if (String(row?.source || '').toLowerCase() === 'platform') return false;
  return rowCanEditScopedTemplate(row);
}

/** 模板库 · 尺码库（对齐证书库列表布局） */
export default function SizeChartTemplatePage() {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [] } = useTiktokPageShopScope();
  const {
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
    categoryOptions,
    categories,
    categoriesLoading,
    ensureCategoriesLoaded,
  } = useTiktokTemplateScope({ preferredShopId: pageShopScope });



  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => resolveWriteShopId({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    title,
    description,
    confirmText,
  }), [listAllShops, pageShopScope, listShopId, pickShop, t]);

  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [styleNoDraft, setStyleNoDraft] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const [editingShopId, setEditingShopId] = useState('');
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [copyingId, setCopyingId] = useState(null);
  const [batchCopying, setBatchCopying] = useState(false);
  const [moving, setMoving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const modalShopId = editingShopId || listShopId;
  const tableColSpan = isOfficialTab ? COL_SPAN_OFFICIAL : COL_SPAN_FULL;
  const { runBlocking, isBusy } = useTiktokSyncJobs();
  const syncBusy = Boolean(listShopId) && !isOfficialTab && isBusy('sizeCharts', listShopId);

  const load = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setItems([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return false;
    }
    setLoading(true);
    try {
      const nameTokens = templateNameTokens(keyword);
      const baseParams = {
        scope: scopeTab,
        styleNo: styleNo || undefined,
        productTypeId: productTypeId || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      };
      if (nameTokens.length > 1) {
        const all = await fetchAllPages(async (pageNo, size) => {
          const res = await tiktokPlatformAPI.listSizeChartTemplates(listShopId, {
            ...baseParams,
            page: pageNo,
            pageSize: size,
          });
          return {
            items: Array.isArray(res?.items) ? res.items : [],
            total: Number(res?.total) || 0,
          };
        });
        const filtered = all.filter((row) => matchTemplateName(row.name, nameTokens));
        const start = (Math.max(1, page) - 1) * pageSize;
        setItems(filtered.slice(start, start + pageSize));
        setTotal(filtered.length);
      } else {
        const res = await tiktokPlatformAPI.listSizeChartTemplates(listShopId, {
          ...baseParams,
          keyword: nameTokens[0] || undefined,
          page,
          pageSize,
        });
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total) || 0);
      }
      return true;
    } catch (e) {
      setItems([]);
      setTotal(0);
      const missing = Number(e?.status) === 404 || /不存在|not found/i.test(String(e?.message || ''));
      if (!missing) showToast(e?.message || t('productTemplate.loadFailed'), 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId, page, pageSize, queryNonce, t]);

  // 进页默认拉本地尺码库
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedKeys.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
  }, [items, selectedKeys]);

  const selectableKeys = useMemo(
    () => items
      .filter((row) => rowCanDeleteScopedTemplate(row) || rowCanCopyScopedTemplate(row))
      .map(rowKey)
      .filter(Boolean),
    [items],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCategoryChange = (id) => {
    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : '';
    setCategoryIdDraft(id ? String(id) : '');
    setProductTypeIdDraft(nextTypeId);
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setStyleNo(styleNoDraft.trim());
    setProductTypeId(productTypeIdDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setStyleNoDraft('');
    setStyleNo('');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setProductTypeId('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resolveSelectedRows = useCallback(() => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  }, [selectedKeys]);

  const resolvePageRows = useCallback(() => items, [items]);

  const resolveFilteredRows = useCallback(async () => {
    if (!canQuery || !listShopId) return [];
    const nameTokens = templateNameTokens(keyword);
    const baseParams = {
      scope: scopeTab,
      styleNo: styleNo || undefined,
      productTypeId: productTypeId || undefined,
      ...(listAllShops ? { allShops: true } : {}),
    };
    if (nameTokens.length > 1) {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await tiktokPlatformAPI.listSizeChartTemplates(listShopId, {
          ...baseParams,
          page: pageNo,
          pageSize: size,
        });
        return {
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total) || 0,
        };
      });
      return all.filter((row) => matchTemplateName(row.name, nameTokens));
    }
    return fetchAllPages(async (pageNo, size) => {
      const res = await tiktokPlatformAPI.listSizeChartTemplates(listShopId, {
        ...baseParams,
        keyword: nameTokens[0] || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId]);

  const {
    exporting,
    onExportCsvFiltered,
    onExportCsvPage,
    onExportCsvSelected,
  } = useMaterialTemplatePortableActions({
    type: MATERIAL_TEMPLATE_TYPES.sizeCharts,
    shopId: listShopId,
    filePrefix: t('sizeChart.filePrefix'),
    resolvePageRows,
    resolveSelectedRows,
    resolveFilteredRows,
    buildCsvRows: (rows) => rows.map((row) => ({
      [t('productTemplate.colPriority')]: row.sortOrder ?? 0,
      [t('productTemplate.colStyleNo')]: row.styleNo || '',
      [t('productTemplate.colName')]: row.name || '',
      [t('productTemplate.colCategory')]: row.categoryPathLabel || row.productTypeId || '',
      [t('productTemplate.colAutoRule')]: row.autoRuleEnabled ? t('productTemplate.on') : t('productTemplate.off'),
      [t('sizeChart.specRows')]: row.sizeRowCount ?? '',
      [t('sizeChart.sizeAttrs')]: row.measureAttrCount ?? '',
      [t('productTemplate.colCreatedAt')]: formatBeijingDateTime(row.createdAt),
      [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.updatedAt || row.platformUpdatedAt),
    })),
    getOne: (id) => tiktokPlatformAPI.getSizeChartTemplate(listShopId, id, { scope: scopeTab }),
    listByProductType: async (productTypeId, ctx) => {
      const sid = ctx?.shopId || listShopId;
      const res = await tiktokPlatformAPI.listSizeChartTemplates(sid, {
        scope: scopeTab,
        productTypeId: productTypeId || undefined,
        page: 1,
        pageSize: 200,
      });
      return Array.isArray(res?.items) ? res.items : [];
    },
    createOne: (body, ctx) => tiktokPlatformAPI.createSizeChartTemplate(ctx?.shopId || listShopId, {
      ...body,
      ...(body.scope ? {} : { scope: scopeTab }),
    }),
    onImported: async () => {
      setSelectedKeys(new Set());
      setPage(1);
      setQueryNonce((n) => n + 1);
    },
  });

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdDelete'), 'error');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('returnList.batchDelete'),
      message: t('sizeChart.batchDeleteMsg', { count: ids.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await tiktokPlatformAPI.batchDeleteSizeChartTemplates(listShopId, ids);
      const deleted = Number(res?.deleted);
      showToast(t('productTemplate.deletedCount', { count: Number.isFinite(deleted) ? deleted : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    setBatchCopying(true);
    try {
      const res = await tiktokPlatformAPI.batchCopySizeChartTemplates(writeShopId, {
        ids,
        sourceScope: scopeTab,
        targetScope,
      });
      const copied = Number(res?.copied);
      showToast(t('productTemplate.copiedCount', { count: Number.isFinite(copied) ? copied : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchCopyFailed'), 'error');
    } finally {
      setBatchCopying(false);
    }
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdMove'), 'error');
      return;
    }
    setMoving(true);
    try {
      const res = await tiktokPlatformAPI.moveSizeChartTemplates(listShopId, {
        ids,
        targetScope,
      });
      const moved = Number(res?.moved);
      showToast(t('productTemplate.movedCount', { count: Number.isFinite(moved) ? moved : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  const onDeleteOne = async (row) => {
    if (!canQuery || !row?.id || !rowCanDeleteScopedTemplate(row)) return;
    const ok = await showTiktokConfirm({
      title: t('productTemplate.deleteTitle'),
      message: t('productTemplate.deleteOne', { name: row.name || row.id }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await tiktokPlatformAPI.deleteSizeChartTemplate(resolveTemplateRowShopId(row, listShopId), row.id);
      showToast(t('productTemplate.deleted'), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onCopyOne = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId || submittingId != null) return;
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await tiktokPlatformAPI.copySizeChartTemplate(writeShopId, {
        sourceId: row.id,
        sourceScope: resolveCopySourceScope(row, { isOfficialTab, scopeTab }),
        targetScope: resolvedTarget,
        // 官方复制到店铺保留原名；重名时由后续编辑/再建处理
        name: String(row.name || '').trim() || undefined,
      });
      showToast(
        isOfficialTab
          ? `${templateCopiedToast(resolvedTarget)}${t('sizeChart.copiedDraft')}`
          : templateCopiedToast(resolvedTarget),
        'success',
      );
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onSubmitToPlatform = async (row) => {
    if (!canQuery || !canSubmitLocalSizeChart(row, { isOfficialTab }) || submittingId != null) return;
    const shopId = resolveTemplateRowShopId(row, listShopId);
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('sizeChart.submitTitle'),
      message: t('sizeChart.submitTiktok', { name: row.name || row.id }),
      confirmText: t('sizeChart.confirmSubmit'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSubmittingId(row.id);
    try {
      const detail = await tiktokPlatformAPI.getSizeChartTemplate(shopId, row.id, {
        scope: row.scope || scopeTab,
      });
      const item = detail?.item || detail || {};
      const { sizeChartId, alreadySynced } = await submitLocalTiktokSizeChartToPlatform({
        shopId,
        item,
        api: tiktokPlatformAPI,
      });
      // 提交成功：清理本地行（平台库可通过「拉取后台」同步回来）
      await tiktokPlatformAPI.deleteSizeChartTemplate(shopId, row.id);
      showToast(
        alreadySynced
          ? t('sizeChart.linkedCleaned', { id: sizeChartId })
          : t('sizeChart.submittedCleaned', { id: sizeChartId }),
        'success',
      );
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('sizeChart.submitFailed'), 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  const onToggleAutoRule = async (row, enabled) => {
    if (!canQuery || !row?.id || !rowCanEditScopedTemplate(row)) return;
    const id = row.id;
    const key = String(id);
    const prevEnabled = row.autoRuleEnabled === true || row.autoRuleEnabled === 1;
    setTogglingIds((prev) => new Set(prev).add(key));
    setItems((prev) => prev.map((item) => (
      String(item.id) === key ? { ...item, autoRuleEnabled: enabled } : item
    )));
    try {
      const body = { autoRuleEnabled: enabled };
      const version = parseTemplateVersion(row.version);
      if (version != null) body.version = version;
      const res = await tiktokPlatformAPI.updateSizeChartTemplate(resolveTemplateRowShopId(row, listShopId), id, body);
      const nextVersion = parseTemplateVersion(res?.item?.version);
      setItems((prev) => prev.map((item) => (
        String(item.id) === key
          ? {
            ...item,
            autoRuleEnabled: enabled,
            ...(nextVersion != null ? { version: nextVersion } : {}),
          }
          : item
      )));
      showToast(enabled ? t('productTemplate.autoOn') : t('productTemplate.autoOff'), 'success');
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        String(item.id) === key ? { ...item, autoRuleEnabled: prevEnabled } : item
      )));
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'sizeChart' }]), 'warning', 3000);
        try {
          const res = await tiktokPlatformAPI.getSizeChartTemplate(resolveTemplateRowShopId(row, listShopId), id, { scope: row.scope || scopeTab });
          const item = res?.item || res || {};
          const serverEnabled = item.autoRuleEnabled === true || item.autoRuleEnabled === 1;
          const serverVersion = parseTemplateVersion(item.version);
          setItems((prev) => prev.map((itemRow) => (
            String(itemRow.id) === key
              ? {
                ...itemRow,
                autoRuleEnabled: serverEnabled,
                ...(serverVersion != null ? { version: serverVersion } : {}),
              }
              : itemRow
          )));
        } catch {
          // 单行刷新失败时保持回滚状态
        }
      } else {
        showToast(e?.message || t('productTemplate.autoUpdateFailed'), 'error');
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const emptyHint = !canQuery
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (keyword || styleNo || productTypeId
        ? t('sizeChart.noMatch')
        : (isOfficialTab
          ? t('sizeChart.emptyOfficial')
          : t('sizeChart.emptyLocal'))));

  const handleSync = useCallback(async () => {
    if (isOfficialTab) return;
    const writeShopId = await ensureWriteShopId({
      title: t('sizeChart.syncPickTitle'),
      description: t('sizeChart.syncPickDesc'),
      confirmText: t('sizeChart.syncHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const syncRes = await runBlocking({
        kind: 'sizeCharts',
        shopId: writeShopId,
        label: t('sizeChart.syncLabel'),
        syncFn: () => tiktokPlatformAPI.syncShopSizeCharts(writeShopId),
        buildSuccess: (res) => {
          const count = Number(res?.imported ?? res?.count);
          return {
            imported: Number.isFinite(count) ? count : 0,
            message: Number.isFinite(count)
              ? t('sizeChart.syncedCount', { count })
              : (res?.message || t('sizeChart.syncDone')),
            silent: true,
          };
        },
      });
      setPage(1);
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setQueryNonce((n) => n + 1);
      if (syncRes?.syncedAt) setLastSyncedAt(String(syncRes.syncedAt));
      else setLastSyncedAt(new Date().toISOString());
      const count = Number(syncRes?.imported ?? syncRes?.count);
      showToast(
        Number.isFinite(count) ? t('sizeChart.syncedCount', { count }) : (syncRes?.message || t('sizeChart.syncDone')),
        'success',
      );
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      showToast(e?.message || t('sizeChart.syncFailed'), 'error');
    }
  }, [isOfficialTab, ensureWriteShopId, runBlocking, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncBusy || syncingLocal;
  const lastSyncedLabel = lastSyncedAt
    ? t('returnList.lastUpdate', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : t('returnList.notSynced');

  return (
    <>
      {!isOfficialTab ? <TiktokSyncProgressSlot kinds={SIZE_CHART_SYNC_KINDS} /> : null}
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || deleting || exporting || batchCopying || moving || syncing}
            batchCopying={batchCopying}
            batchMoving={moving}
            syncKinds={isOfficialTab ? [] : SIZE_CHART_SYNC_KINDS}
            quotaKind="sizeCharts"
            onSync={isOfficialTab ? undefined : guardedSync}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!canQuery || loading || deleting || exporting || syncing}
            lastSyncedLabel={isOfficialTab ? '' : lastSyncedLabel}
            syncHint={t('sizeChart.syncHintTiktok')}
            batchOpsMenu={buildTiktokWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || deleting || exporting || isOfficialTab,
            })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!canQuery || loading || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'create',
                label: t('sizeChart.create'),
                tone: 'primary',
                disabled: !canQuery || loading || deleting || exporting || syncing,
                onClick: async () => {
                  const writeShopId = await ensureWriteShopId({
                    title: t('productTemplate.createPickTitle'),
                    description: t('sizeChart.createPickDesc'),
                    confirmText: t('productTemplate.createHere'),
                  });
                  if (!writeShopId) {
                    showToast(t('returnList.pickShop'), 'warning');
                    return;
                  }
                  setEditingId(null);
                  setEditingShopId(writeShopId);
                  setModalOpen(true);
                },
              },
            ]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--category">
              <span className="tko-field__label">{t('productTemplate.colCategory')}</span>
              <CategoryCascaderSelect
                shopId={listShopId}
                categories={categories}
                value={categoryIdDraft}
                disabled={!canQuery || loading}
                placeholder={!canQuery ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                emptyText={
                  !canQuery
                    ? t('returnList.pickShop')
                    : (categoriesLoading ? t('productTemplate.categoriesLoading') : t('productTemplate.noCategories'))
                }
                onOpen={ensureCategoriesLoaded}
                onChange={onCategoryChange}
              />
            </div>
            <TiktokStyleNoBatchFilter
              value={styleNoDraft}
              disabled={!canQuery}
              onChange={setStyleNoDraft}
              onEnter={runQuery}
            />
            <TiktokTemplateNameBatchFilter
              value={keywordDraft}
              disabled={!canQuery}
              onChange={setKeywordDraft}
              onEnter={runQuery}
            />
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--ghost tko-btn--sm"
              disabled={!canQuery || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!canQuery || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(canQuery) && loading}
        loadingLabel={t('productTemplate.querying')}>
        <TiktokTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('sizeChart.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <div className="tko-drafts__table-wrap tko-certs-table-wrap">
          <table className={`tko-publish__table tko-drafts__table tko-certs-table tko-tpl-table tko-tpl-table--size${isOfficialTab ? ' tko-tpl-table--size-official' : ''}`}>
            <colgroup>
              <col className="tko-certs-col tko-certs-col--check" />
              <col className="tko-certs-col tko-certs-col--order" />
              <col className="tko-certs-col tko-certs-col--style-no" />
              <col className="tko-certs-col tko-certs-col--name" />
              <col className="tko-certs-col tko-certs-col--category" />
              {!isOfficialTab ? <col className="tko-certs-col tko-certs-col--source" /> : null}
              <col className="tko-certs-col tko-certs-col--switch" />
              <col className="tko-certs-col tko-certs-col--filled" />
              <col className="tko-certs-col tko-certs-col--filled" />
              {!isOfficialTab ? <col className="tko-certs-col tko-certs-col--createdAt" /> : null}
              {!isOfficialTab ? <col className="tko-certs-col tko-certs-col--updatedAt" /> : null}
              <col className="tko-certs-col tko-certs-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-certs-th tko-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tko-certs-th tko-certs-th--order">{t('productTemplate.colPriority')}</th>
                <th scope="col" className="tko-certs-th tko-certs-th--style-no">{t('productTemplate.colStyleNo')}</th>
                <th scope="col" className="tko-certs-th tko-certs-th--name">{t('productTemplate.colName')}</th>
                <th scope="col" className="tko-certs-th tko-certs-th--category">{t('productTemplate.colCategory')}</th>
                {!isOfficialTab ? <th scope="col" className="tko-certs-th tko-certs-th--source">{t('sizeChart.source')}</th> : null}
                <th scope="col" className="tko-certs-th tko-certs-th--switch">{t('productTemplate.colAutoRule')}</th>
                <th scope="col" className="tko-certs-th tko-certs-th--filled">{t('sizeChart.specRows')}</th>
                <th scope="col" className="tko-certs-th tko-certs-th--filled">{t('sizeChart.sizeAttrs')}</th>
                {!isOfficialTab ? <th scope="col" className="tko-certs-th tko-certs-th--time">{t('productTemplate.colCreatedAt')}</th> : null}
                {!isOfficialTab ? <th scope="col" className="tko-certs-th tko-certs-th--time">{t('productTemplate.colUpdatedAt')}</th> : null}
                <th scope="col" className="tko-certs-th tko-certs-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || items.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                const canEdit = rowCanEditScopedTemplate(row);
                const canDelete = rowCanDeleteScopedTemplate(row);
                const canCopy = rowCanCopyScopedTemplate(row);
                const canSelect = canDelete || canCopy;
                const rowShopId = resolveTemplateRowShopId(row, listShopId);
                return (
                  <tr
                    key={key}
                    className={canSelect ? 'tko-row--clickable' : undefined}
                    onClick={(e) => {
                      if (!canSelect) return;
                      if (e.target.closest('button, a, input, .tko-copy-hover, .tiktok-draft-actions')) return;
                      toggleOne(key);
                    }}
                  >
                    <td className="tko-certs-td tko-certs-td--check">
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        disabled={!canSelect}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          if (!canSelect) return;
                          toggleOne(key);
                        }}
                      />
                    </td>
                    <td className="tko-certs-td tko-certs-td--order">{row.sortOrder ?? 0}</td>
                    <td
                      className="tko-certs-td tko-certs-td--style-no"
                      title={cellDisplayTitle(row.styleNo)}
                    >
                      {row.styleNo || '—'}
                    </td>
                    <td
                      className="tko-certs-td tko-certs-td--name"
                      title={cellDisplayTitle(row.name, row.remark)}
                    >
                      <strong>{row.name || '—'}</strong>
                      {row.remark ? <div className="tko-muted">{row.remark}</div> : null}
                    </td>
                    <td
                      className="tko-certs-td tko-certs-td--category"
                      title={cellDisplayTitle(row.categoryPathLabel || row.productTypeId)}
                    >
                      {row.categoryPathLabel || row.productTypeId || '—'}
                    </td>
                    {!isOfficialTab ? (
                      <td className="tko-certs-td tko-certs-td--source" title={cellDisplayTitle(sourceLabel(row))}>
                        {sourceLabel(row)}
                      </td>
                    ) : null}
                    <td className="tko-certs-td tko-certs-td--switch">
                      <TiktokSwitch
                        checked={!!row.autoRuleEnabled}
                        disabled={!canEdit || deleting || togglingIds.has(key)}
                        ariaLabel={t('productTemplate.autoRuleAria', { name: row.name || t('productTemplate.templateFallback') })}
                        onChange={(next) => onToggleAutoRule(row, next)}
                      />
                    </td>
                    <td className="tko-certs-td tko-certs-td--filled">{row.sizeRowCount ?? '—'}</td>
                    <td className="tko-certs-td tko-certs-td--filled">{row.measureAttrCount ?? '—'}</td>
                    {!isOfficialTab ? (
                      <td
                        className="tko-certs-td tko-certs-td--time"
                        title={cellDisplayTitle(formatBeijingDateTime(row.createdAt))}
                      >
                        {formatBeijingDateTime(row.createdAt)}
                      </td>
                    ) : null}
                    {!isOfficialTab ? (
                      <td
                        className="tko-certs-td tko-certs-td--time"
                        title={cellDisplayTitle(formatBeijingDateTime(row.updatedAt || row.platformUpdatedAt))}
                      >
                        {formatBeijingDateTime(row.updatedAt || row.platformUpdatedAt)}
                      </td>
                    ) : null}
                    <td className="tko-certs-td tko-certs-td--ops">
                      <div className="tiktok-draft-actions">
                        {canEdit ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null || submittingId != null}
                            onClick={() => {
                              setEditingId(row.id);
                              setEditingShopId(rowShopId);
                              setModalOpen(true);
                            }}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {canSubmitLocalSizeChart(row, { isOfficialTab }) ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null || submittingId != null}
                            onClick={() => onSubmitToPlatform(row)}
                          >
                            {submittingId === row.id ? t('returnList.submitting') : t('sizeChart.submit')}
                          </button>
                        ) : null}
                        {canCopy ? (
                          <TiktokOpsCopyHoverMenu
                            disabled={deleting || copyingId != null || submittingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOne(row, target),
                            })}
                          />
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null || submittingId != null}
                            onClick={() => onDeleteOne(row)}
                          >
                            {t('returnList.delete')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('sizeChart.pager')}
        />
      </ProductListCard>

      <SizeChartTemplateCreateModal
        open={modalOpen}
        shopId={modalShopId}
        templateId={editingId}
        templateScope={scopeTab}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setEditingShopId('');
        }}
        onSaved={() => {
          setPage(1);
          setQueryNonce((n) => n + 1);
        }}
      />
      {shopPickModal}
    </>
  );
}
